<script lang="ts" setup>
import FilterTable from "@/components/Common/Table/FilterTable.vue";
import { ref } from "vue";
import type { Header } from "vue3-easy-data-table";
import EasyDataTable from "vue3-easy-data-table";
import "vue3-easy-data-table/dist/style.css";
import Swal from "sweetalert2";
import router from "@/router";
import BaseLoading from "@/components/Common/BaseLoading.vue";
import { useRoleStore } from "@/stores/master/role";
import { toast } from "vue-sonner";
import { hasAccess } from "@/utils/helper";

const linkAdd = {
  text: "Add Role",
  url: "/master/role/create",
};

const headers: Header[] = [
  { text: "CODE", value: "code" },
  { text: "ROLE", value: "name" },
  { text: "ROLE GROUP", value: "role_group_name" },
  { text: "ACTIONS", value: "action" },
];

const roleStore = useRoleStore();
const page = ref<number>(1);
const limit = ref<number>(10);
const totalItems = ref<number>(0);
const listRole = ref<any>([]);
const isLoading = ref(false);

const loadDataRole = async (query = "") => {
  isLoading.value = true;
  const { success, message } = await roleStore.dispatchGetRoles(
    page.value,
    limit.value,
    query
  );
  if (success) {
    listRole.value = await roleStore.roles.items;
    page.value = await roleStore.roles.page;
    limit.value = await roleStore.roles.pageSize;
    totalItems.value = await roleStore.roles.total;
  }
  isLoading.value = false;
};

const handleServerOptionsUpdate = (options: {
  page: number;
  rowsPerPage: number;
}) => {
  page.value = options.page;
  limit.value = options.rowsPerPage;
  loadDataRole();
};

const handleEdit = (role: any) => {
  router.push("/master/role/" + role);
};

const handleAccess = (role: any) => {
  router.push("/master/role/access/" + role);
};

const handleMenu = (role: any) => {
  router.push("/master/role/menu/" + role);
};

const handleDelete = async (role: any) => {
  const swalWithBootstrapButtons = Swal.mixin({
    customClass: {
      cancelButton: "btn btn-danger",
      confirmButton: "btn btn-success",
    },
    buttonsStyling: false,
  });
  swalWithBootstrapButtons
    .fire({
      title: `Are you sure want to delete data ${role.name} ?`,
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
      reverseButtons: true,
    })
    .then(async (result) => {
      if (result.isConfirmed) {
        const { success, message } = await roleStore.dispatchDelete(role.id);
        if (success) {
          swalWithBootstrapButtons.fire({
            title: "Deleted!",
            text: "Your file has been deleted.",
            icon: "success",
          });
          loadDataRole();
        } else {
          toast.error(message);
        }
      } else if (
        /* Read more about handling dismissals below */
        result.dismiss === Swal.DismissReason.cancel
      ) {
        swalWithBootstrapButtons.fire({
          title: "Cancelled",
          text: "Your action has been aborted",
          icon: "error",
        });
      }
    });
};

loadDataRole();
</script>
<template>
  <div class="card bg-white border-0 rounded-3 mb-4">
    <div class="card-body p-4">
      <div
        class="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3 mb-lg-4"
      >
        <h3 class="mb-0">List Data Role</h3>
      </div>
      <FilterTable :link="linkAdd" @searchByQuery="loadDataRole" />
      <div class="table-content">
        <BaseLoading v-if="isLoading" />
        <EasyDataTable
          :headers="headers"
          :items="listRole"
          v-if="!isLoading"
          show-index
          :server-options="{
            page: page,
            rowsPerPage: limit,
          }"
          :server-items-length="totalItems"
          @update:server-options="handleServerOptionsUpdate"
          buttons-pagination
        >
          <template #item-action="props">
            <div class="d-flex gap-2">
              <button
                v-if="hasAccess('access_permission-assign_by_role')"
                class="btn btn-primary btn-sm text-white"
                @click="handleAccess(props.id)"
              >
                Access
              </button>
              <button
                class="btn btn-secondary btn-sm text-white"
                @click="handleMenu(props.id)"
              >
                Menu
              </button>
              <button
                v-if="hasAccess('role-update')"
                class="btn btn-info btn-sm text-white"
                @click="handleEdit(props.id)"
              >
                Edit
              </button>
              <button
                v-if="hasAccess('role-update')"
                class="btn btn-danger btn-sm"
                @click="handleDelete(props)"
              >
                Delete
              </button>
            </div>
          </template>
        </EasyDataTable>
      </div>
    </div>
  </div>
</template>
